import React, { FC, useCallback, useMemo } from 'react';

import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import usePermissions from 'api/usePermissions';
import { EIntervalsRepeat } from 'constants/enums';
import {
  dayInMonthOptions,
  intervalsRepeatOptions,
  roundedHoursOptions
} from 'utils/selectOptionsUtils';

import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';

import { AvailabilityInput } from './AvailabilityInput';
import { HourField } from './HourField';

interface IScheduleIntervalProps {
  repeat: EIntervalsRepeat;
  setRepeat: (repeat: EIntervalsRepeat) => void;
  startHour: string;
  setStartHour: (hour: string) => void;
  weekDay: number;
  setWeekDay: (day: number) => void;
  dayInMonth: number;
  setDayInMonth: (day: number) => void;
}

const WEEK_DAYS_OPTIONS = [
  { label: 'Every Sunday', value: 0 },
  { label: 'Every Monday', value: 1 },
  { label: 'Every Tuesday', value: 2 },
  { label: 'Every Wednesday', value: 3 },
  { label: 'Every Thursday', value: 4 },
  { label: 'Every Friday', value: 5 },
  { label: 'Every Saturday', value: 6 }
];

const ScheduleInterval: FC<IScheduleIntervalProps> = ({
  repeat,
  setRepeat,
  startHour,
  setStartHour,
  weekDay,
  setWeekDay,
  dayInMonth,
  setDayInMonth
}): React.ReactElement => {
  const form = useFormContext();
  const { publisherId } = useParams();
  const { isSuperAdminByProject } = usePermissions();
  const isSuperAdmin = publisherId && isSuperAdminByProject(publisherId);

  const handleRepeatChange = useCallback(
    (value: string): void => {
      setRepeat(value as EIntervalsRepeat);
    },
    [setRepeat]
  );

  const handleStartHourChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setStartHour(e.target.value);
      form.setValue('startHour', e.target.value, { shouldDirty: true });
    },
    [setStartHour, form]
  );

  const handleStartHourSelectChange = useCallback(
    (value: string): void => {
      setStartHour(value);
      form.setValue('startHour', value, { shouldDirty: true });
    },
    [setStartHour, form]
  );

  const handleStartHourBlur = useCallback((): void => {
    form.trigger('startHour');
  }, [form]);

  const handleWeekDayChange = useCallback(
    (value: string): void => {
      setWeekDay(Number(value));
    },
    [setWeekDay]
  );

  const handleDayInMonthChange = useCallback(
    (value: string): void => {
      setDayInMonth(Number(value));
    },
    [setDayInMonth]
  );

  const selectedRepeatOption = useMemo(
    () => intervalsRepeatOptions.find((option) => option.value === repeat),
    [repeat]
  );

  const selectedStartHourOption = useMemo(
    () => roundedHoursOptions.find((option) => option.value === startHour),
    [startHour]
  );

  const selectedWeekDayOption = useMemo(
    () => WEEK_DAYS_OPTIONS.find((option) => option.value === weekDay),
    [weekDay]
  );

  const selectedDayInMonthOption = useMemo(
    () => dayInMonthOptions.find((option) => option.value === dayInMonth),
    [dayInMonth]
  );

  const startHourError = form.formState.errors.startHour;
  const hasStartHourError = !!startHourError;

  const showHourField = repeat !== EIntervalsRepeat.NONE;
  const showWeekDayField =
    repeat === EIntervalsRepeat.WEEKLY || repeat === EIntervalsRepeat.MONTHLY;
  const showMonthDayField = repeat === EIntervalsRepeat.MONTHLY;

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex gap-5">
        <AvailabilityInput id="availability-intervals" />
        <div className="w-full">
          <InputSelectDropdown
            label="Repeat"
            listDataTestId="intervalRepeatList"
            buttonDataTestId="intervalRepeatButton"
            options={intervalsRepeatOptions}
            defaultSelected={selectedRepeatOption}
            onOptionChange={handleRepeatChange}
          />
        </div>
      </div>
      <div className="flex gap-5">
        {showHourField && (
          <HourField
            isSuperAdmin={!!isSuperAdmin}
            startHour={startHour}
            onInputChange={handleStartHourChange}
            onBlur={handleStartHourBlur}
            hasError={hasStartHourError}
            errorMessage={String(startHourError?.message || '')}
            selectedOption={selectedStartHourOption}
            onSelectChange={handleStartHourSelectChange}
          />
        )}
        {showWeekDayField && (
          <div className="w-[160px]">
            <InputSelectDropdown
              label="Week Day"
              options={WEEK_DAYS_OPTIONS}
              defaultSelected={selectedWeekDayOption}
              onOptionChange={handleWeekDayChange}
            />
          </div>
        )}
        {showMonthDayField && (
          <div className="w-[160px]">
            <InputSelectDropdown
              label="Month Day"
              options={dayInMonthOptions}
              defaultSelected={selectedDayInMonthOption}
              onOptionChange={handleDayInMonthChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export { ScheduleInterval };
